<!--
 * @Author       : 花裤衩<白永明><yongming.bai@outlook.com>
 * @Date         : 2025-08-05 17:38:56 +0800
 * @LastEditors  : 花裤衩<白永明><yongming.bai@outlook.com>
 * @LastEditTime : 2026-01-26 09:48:46 +0800
 * @FilePath     : /izt_web/src/views/ai/components/common/hold-to-talk.vue
 * @Description  : 按住说话
-->
<template>
  <div ref="holdToTalkRef" class="hold-to-talk-container" @contextmenu.prevent>
    <div ref="cancelRef" class="cancel-region" :class="{ active: isCanceled }" v-show="isRecording">
      <span>{{ isCanceled ? cancelText : operationText }}</span>
    </div>
    <toast v-if="currentDuration >= 50" :duration="0" :content="countdownText" :key="countdownText" :bottom="`${configHeight + 70}px`"/>
    <button
      class="record-btn"
      :class="{'disabled-style': !disabled}"
      @mousedown="startRecording"
      @mouseup="stopRecording"
      @mouseleave="cancelRecording"
      @touchstart.prevent="startRecording"
      @touchend.prevent="stopRecording"
      @touchcancel.prevent="cancelRecording"
    >
      <template v-if="isRecording || loading">
        <hold-talk-wave v-if="isRecording || loading" :loading.sync="loading"/>
      </template>
      <template v-else>
        {{holdToTalk}}
      </template>
    </button>
  </div>
</template>

<script>
import holdTalkWave from './hold-talk-wave.vue'
import { audioIdentify } from '@/common/http/ai'
import { Toast } from './Toast'
import { mapGetters } from 'vuex'
import toast from './Toast/Toast.vue';
export default {
  name: 'hold-to-talk',
  components: {
    holdTalkWave,
    toast
  },
  props: {
    disabled: { Boolean }
  },
  data() {
    return {
      configHeight: 0,
      startTime: 0,               // 按下时间戳
      endTime: 0,                 // 松开时间戳 用户校验单词说话长度是否合法
      maxPressTimer: null,        // 超时自动触发
      durationSpeech: 60 * 1000,  // 单词说话时长
      timerInterval: null,        // 每秒更新一次计时
      currentDuration: 0,         // 当前说话的秒数
      loading: false,             // 语言转文字的加载状态
      isRecording: false,         // 是否按下
      isCanceled: false,          // 是否取消
      audioContext: null,
      mediaStreamSource: null,
      processorNode: null,
      mediaStream: null,
      audioDataBuffer: [],
      sampleRate: 44100,          // 44100 请勿随意修改
      useAudioWorklet: false,     // 是否开始录音
    }
  },

  computed: {
    ...mapGetters(['lang']),
    countdownText() {
      return `${(this.durationSpeech / 1000) - this.currentDuration}s后将停止录音 `
    },
    // 按住说话
    holdToTalk() {
      const text = {
        cn: '按住说话',
        en: 'Hold to Talk'
      }[this.lang]
      return text || '按住说话'
    },
    // 取消提示
    cancelText() {
      const text = {
        cn: '松手取消发送',
        en: 'Release to Cancel'
      }[this.lang]
      return text || '松手取消发送'
    },
    // 操作提示
    operationText() {
      const text = {
        cn: '松手发送 上滑取消',
        en: 'Release to Send · Slide Up to Cancel'
      }[this.lang]
      return text || '松手发送 上滑取消'
    },
  },

  mounted() {
    document.addEventListener('contextmenu', this.disableRightClick)
    document.addEventListener('mousedown', this.disableMouseRightClick)


    const el = this.$refs.holdToTalkRef;
    if (el) {
      // 1. 元素自身高度（包括小数像素）
      const height = el.getBoundingClientRect().height;
      // 2. 元素底部到窗口底部的距离
      const bottomDistance = window.innerHeight - el.getBoundingClientRect().bottom;

      this.configHeight = (height + bottomDistance + 10)
      this.$refs.cancelRef.style.bottom = `${this.configHeight}px`;
    }
  },
  beforeDestroy() {
    document.removeEventListener('contextmenu', this.disableRightClick)
    document.removeEventListener('mousedown', this.disableMouseRightClick)
  },
  methods: {
    // 按住说话
    async startRecording() {
      if (this.isRecording || !this.disabled) return
      this.startTime = Date.now()
      if (this.timerInterval) clearInterval(this.timerInterval)
      this.currentDuration = 0
      this.timerInterval = setInterval(() => {
        this.currentDuration = Math.floor((Date.now() - this.startTime) / 1000)
      }, 1000)
      try {
        this.isCanceled = false;
        this.$emit('speaking', true)
        this.isRecording = true
        this.audioDataBuffer = []

        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        this.sampleRate = this.audioContext.sampleRate
        this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.mediaStream)

        window.addEventListener('mousemove', this.checkCancelRegion)
        window.addEventListener('touchmove', this.checkCancelRegion, { passive: false })
        await this.setupProcessor()
        // 开始1分钟倒计时（60000ms）
        this.maxPressTimer = setTimeout(() => {
          this.stopRecording() // 传true表示是超时触发的
        }, this.durationSpeech)
      } catch (err) {
        clearInterval(this.timerInterval)
        clearTimeout(this.maxPressTimer)
        console.error('录音启动失败:', err)
        this.isRecording = false
      }
    },
    // 录音开始
    async setupProcessor() {
      this.useAudioWorklet = false
      try {
        if (this.audioContext.audioWorklet) {
          await this.audioContext.audioWorklet.addModule('./recorder-worklet-processor.js')
          this.processorNode = new AudioWorkletNode(this.audioContext, 'recorder-processor')
          this.processorNode.port.onmessage = (event) => {
            const chunk = new Float32Array(event.data)
            this.audioDataBuffer.push(chunk)
          }
          this.useAudioWorklet = true
        }
      } catch (err) {
        console.warn('AudioWorklet 降级:', err)
        this.useAudioWorklet = false
      }

      if (!this.useAudioWorklet) {
        this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1)
        this.processorNode.onaudioprocess = (e) => {
          const input = e.inputBuffer.getChannelData(0)
          this.audioDataBuffer.push(new Float32Array(input))
        }
      }

      this.mediaStreamSource.connect(this.processorNode)
      this.processorNode.connect(this.audioContext.destination) // ❌
      this.processorNode.connect(this.audioContext.createGain()) // ✅ 仅处理，不输出到扬声器
    },
    // 松手处理
    async stopRecording() {
      if (!this.isRecording) return
      this.endTime = Date.now()
      this.isRecording = false

      try {
        // 松手处理
        if (this.timerInterval) {
          clearInterval(this.timerInterval)
          this.timerInterval = null
        }
        this.currentDuration = 0
        if (this.maxPressTimer) {
          clearTimeout(this.maxPressTimer)
          this.maxPressTimer = null
        }
        // 移除监听
        window.removeEventListener('mousemove', this.checkCancelRegion)
        window.removeEventListener('touchmove', this.checkCancelRegion)
        this.$emit('speaking', false)

        // 校验
        const duration = this.endTime - this.startTime
        const isAtLeastOneSecond = duration >= 1000
        if (this.isCanceled) {
          // 已滑到取消区域，不发送
          setTimeout(() => this.resetAudio(), 50);
          return
        } else if(!isAtLeastOneSecond) {
          setTimeout(() => this.resetAudio(), 500);
          return Toast({ content: '说话时间太短', bottom: `${this.configHeight + 70}px` });
        }

        // 转码 & 播放 & 上传
        const merged = this.flattenArray(this.audioDataBuffer)
        const wavBlob = this.encodeWAV(merged, this.sampleRate)

        this.uploadWav(wavBlob)

      } catch (err) {
        console.error('录音停止失败:', err)
      }
    },

    // 取消区域判断
    checkCancelRegion(e) {
      const cancelEl = this.$refs.cancelRef
      if (!cancelEl) return

      const rect = cancelEl.getBoundingClientRect()
      let x, y

      if (e.touches && e.touches.length > 0) {
        x = e.touches[0].clientX
        y = e.touches[0].clientY
      } else {
        x = e.clientX
        y = e.clientY
      }

      const inRegion =
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom

      this.isCanceled = inRegion
    },

    cancelRecording() {
      if (this.isRecording) {
        this.stopRecording()
      }
    },
    // 二进制
    flattenArray(bufferArray) {
      const length = bufferArray.reduce((sum, chunk) => sum + chunk.length, 0)
      const result = new Float32Array(length)
      let offset = 0
      bufferArray.forEach(chunk => {
        result.set(chunk, offset)
        offset += chunk.length
      })
      return result
    },
    // 录音格式转换
    encodeWAV(samples, sampleRate) {
      const buffer = new ArrayBuffer(44 + samples.length * 2)
      const view = new DataView(buffer)

      const writeString = (view, offset, string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i))
        }
      }

      const floatTo16BitPCM = (output, offset, input) => {
        for (let i = 0; i < input.length; i++, offset += 2) {
          let s = Math.max(-1, Math.min(1, input[i]))
          s = s < 0 ? s * 0x8000 : s * 0x7FFF
          output.setInt16(offset, s, true)
        }
      }

      writeString(view, 0, 'RIFF')
      view.setUint32(4, 36 + samples.length * 2, true)
      writeString(view, 8, 'WAVE')
      writeString(view, 12, 'fmt ')
      view.setUint32(16, 16, true)
      view.setUint16(20, 1, true)
      view.setUint16(22, 1, true)
      view.setUint32(24, sampleRate, true)
      view.setUint32(28, sampleRate * 2, true)
      view.setUint16(32, 2, true)
      view.setUint16(34, 16, true)
      writeString(view, 36, 'data')
      view.setUint32(40, samples.length * 2, true)

      floatTo16BitPCM(view, 44, samples)

      return new Blob([view], { type: 'audio/wav' })
    },

    uploadWav(blob) {
      const formData = new FormData()
      formData.append('file', blob, 'recording.wav')
      this.loading = true
      setTimeout(() => this.resetAudio(), 50);
      audioIdentify(formData).then(({data: result}) => {
        if(result.code === 0) {
          if(result.data.text) {
            this.$emit('send', result.data.text)
          } else {
            Toast({ content: '未识别到文字', message: '请重试', bottom: `${this.configHeight + 70}px` });
          }
        } else {
          Toast({ content: '网络开小差了，请重试', bottom: `${this.configHeight + 70}px` });
        }
      }).catch(() => {
        Toast({ content: '网络开小差了，请重试', bottom: `${this.configHeight + 70}px` });
      }).finally(() => {
        this.startTime = 0
        this.endTime = 0
        this.loading = false
      })
    },
    // 禁止浏览器右击行为
    disableRightClick(e) {
      e.preventDefault()
    },

    async resetAudio() {
      try {
        if (this.processorNode) {
          this.processorNode.disconnect();
          this.processorNode.onaudioprocess = null;
          this.processorNode = null;
        }

        if (this.mediaStreamSource) {
          this.mediaStreamSource.disconnect();
          this.mediaStreamSource = null;
        }

        if (this.mediaStream) {
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
        }

        if (this.audioContext) {
          // 先 suspend 再 close，兼容移动端
          if (this.audioContext.state !== "closed") {
            try {
              await this.audioContext.suspend();
              await this.audioContext.close();
            } catch(e) {
              console.warn("关闭 AudioContext 失败:", e);
            }
          }
          this.audioContext = null;
        }

        this.audioDataBuffer = [];
        this.startTime = 0;
        this.endTime = 0;

        console.log("录音资源已彻底释放");
      } catch (err) {
        console.error("resetAudio 失败:", err);
      }
    },

    disableMouseRightClick(e) {
      if (e.button === 2) {
        e.preventDefault()
        return false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hold-to-talk-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
  user-select: none;
  flex: 1;
  box-sizing: border-box;

  .cancel-region {
    position: fixed;
    z-index: 1;
    font-size: 12px;
    left: 0;
    right: 0;
    top: 0;
    bottom: 70px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    color: rgba(0, 0, 0, 0.65);
    padding-bottom: 10px;
    &.active {
      color: rgb(255, 0, 0);
    }
  }

  .record-btn {
    all: unset;
    width: 100%;
    height: 100%;
    flex: 1!important;
    color: rgba(0, 0, 0, 0.85);
    background-color: #ffffff00;
    border: none;
    border-radius: 6px;
    font-size: 15px;
    transition: background 0.3s;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
  }
  .disabled-style {
    color: #00000072;
  }

  .remaining-words {
    position: fixed;
    z-index: 2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: rgba(255, 255, 255, 1);
    min-width: 130px;
    height: 40px;
    padding: 0 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 4px 8px 0px rgba(7, 41, 177, 0.05);
    background: linear-gradient(270deg, #737EFF 0%, #9F8DFC 100%);
    border-radius: 10px 10px 0px 10px;
  }
}
</style>